<template>
	<v-card class="speaker-session-card elevation-0" :style="wrapperStyle">
		<div
			v-if="timeStatus"
			class="speaker-session-card__time-start"
			:style="{ backgroundColor: timeStatus === 'now' ? '#002855' : '#0055a3' }"
		>
			{{ timeStatus === 'now' ? 'Now' : 'Soon' }}
		</div>
		<SponsorBadge
			v-if="sponsoredLabel"
			:text="sponsoredLabel"
			:top="'0px'"
			:right="'0px'"
			:icon="'star'"
			:iconSize="10"
			:iconColor="'#ffffff'"
		/>
		<div>
			<div :style="{ color: titleSessionColor }" class="speaker-session-card__begin">
				{{ formatTime(session.events_agenda_from, session.agendaTimezone) }}
			</div>
			<div :style="{ color: titleSessionColor }" class="speaker-session-card__end">
				{{ formatTime(session.events_agenda_to, session.agendaTimezone) }}
			</div>
			<div :style="{ color: titleSessionColor }" class="speaker-session-card__end">
				({{ getTimezoneAbbreviation(session.agendaTimezone) }})
			</div>
			<div v-if="tracksCount" class="agenda-card-date__circle-wrapper">
				<div
					v-for="i in tracksCount"
					:key="i"
					class="agenda-card-date__circle"
					:style="{ background: tracksCountColor }"
				/>
			</div>
		</div>
		<div class="speaker-session-card__content-container">
			<p class="speaker-session-card__title mb-1" :style="{ color: titleSessionColor }">
				{{ session.events_agenda_text }}
			</p>
			<p
				:style="{ color: titleColor }"
				class="speaker-session-card__event-location mb-0"
				v-if="session.session_location"
			>
				<Icon type="pin" :color="titleColor" :size="14" :pr="8" />
				{{ session.session_location }}
			</p>
			<div
				v-if="session.session_register_link"
				:style="{ backgroundColor: titleColor }"
				class="agenda-card-seats"
			>
				Limited seats
			</div>
		</div>
		<v-btn
			@click.prevent="toggleMyAgenda"
			class="speaker-session-card__add-agenda-btn elevation-0"
			:loading="isSpeakerSessionLoading && speakerIdLoading === session.events_agenda_id"
		>
			<Icon :type="scheduleIcon.icon" :color="scheduleIcon.color" :size="24" />
		</v-btn>
	</v-card>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import sponsoredStarIcon from '@/assets/images/icon-sponsored-star.svg'
import ProfileBadge from '@/components/ProfileBadge.vue'
import SponsorBadge from '@/components/SponsorBadge.vue'
export default {
	props: {
		session: {
			type: Object,
			default: () => {},
		},
		type: {
			type: String,
			default: '',
		},
	},
	components: {
		SponsorBadge,
		ProfileBadge,
	},
	data() {
		return {
			sponsoredStarIcon,
			circleIndicators: [1, 2, 3, 4, 5, 6],
			isTimeStarted: false,
		}
	},
	methods: {
		...mapActions('user', ['scheduleSession', 'unscheduleSession']),
		formatTime(timestamp, timezone) {
			const options = {
				hour: '2-digit',
				minute: '2-digit',
				hour12: true,
				timeZone: 'UTC',
			}

			const date = new Date(timestamp * 1000)
			const offset = Number(timezone.timezone_utc.slice(-2))
			const sign = timezone.timezone_utc.slice(3, 4)

			if (sign === '-') {
				date.setHours(date.getHours() - offset)
			} else {
				date.setHours(date.getHours() + offset)
			}

			return date.toLocaleTimeString('en-US', options)
		},

		getTimezoneAbbreviation(timezone) {
			return timezone.timezone_code || 'UTC'
		},
		toggleMyAgenda() {
			if (!this.isSpeakerSessionLoading) {
				if (this.session.is_scheduled) {
					this.unscheduleSession({ id: this.session.events_agenda_id, type: this.type })
				} else {
					this.scheduleSession({ id: this.session.events_agenda_id, type: this.type })
				}
			}
		},
	},
	computed: {
		...mapGetters('settings', ['styles']),
		...mapGetters('user', ['speakerIdLoading', 'isSpeakerSessionLoading']),
		timeStatus() {
			const now = new Date()

			const from = new Date(this.session.events_agenda_from * 1000)
			const to = new Date(this.session.events_agenda_to * 1000)
			const offset = Number(this.session.agendaTimezone.timezone_utc.slice(-2))
			const sign = this.session.agendaTimezone.timezone_utc.slice(3, 4)

			if (sign === '+') {
				from.setHours(from.getHours() + offset)
				to.setHours(to.getHours() + offset)
			} else {
				from.setHours(from.getHours() - offset)
				to.setHours(to.getHours() - offset)
			}

			const fifteenMinutesInMs = 15 * 60 * 1000

			if (now >= from && now <= to) {
				return 'now'
			} else if (now >= new Date(from.getTime() - fifteenMinutesInMs) && now < from) {
				return 'soon'
			}
			return ''
		},
		tracksCount() {
			if (this.session && this.session.tracks) {
				return Math.min(this.session.tracks.length, 10)
			}
			return 0
		},
		wrapperStyle() {
			const borderColor = this.timeStatus ? '#0055a3' : '#e2e2e2'

			return {
				paddingTop: this.session.sponsored_by || this.timeStatus ? '32px' : '12px',
				border: `1px solid ${borderColor}`,
			}
		},
    titleColor() {
			return this.type === 'future' ? 'var(--location-color)' : '#9B9B9B'
		},
		tracksCountColor() {
			return this.type === 'future' ? '#674ea6' : '#9B9B9B'
		},
		titleSessionColor() {
			return this.type === 'future' ? 'var(--username-category-title)' : '#9B9B9B'
		},
		scheduleIcon() {
      const defaultColor = this.type === 'future' ? 'var(--button-icon-tint)' : '#9b9b9b'

			return this.session.is_scheduled
				? { icon: 'add_schedule', color: '#9B9B9B' }
				: {
						icon: 'remove_schedule',
						color: defaultColor,
				  }
		},
		sponsoredLabel() {
			return this.session.sponsored_by ? `Sponsored by ${this.session.sponsored_by}` : ''
		},
	},
}
</script>

<style lang="scss">
.speaker-session-card {
	width: 100%;
	border-radius: 8px !important;
	padding: 12px;
	margin-bottom: 8px;
	gap: 12px;
	display: flex;
	flex-wrap: nowrap;
	justify-content: space-between;
	&__content-container {
		width: 100%;
	}
	&__time-start {
		position: absolute;
		top: 0;
		left: 0;
		width: 52px;
		height: 20px;
		background-color: #0055a3;
		font-family: Inter, sans-serif;
		font-weight: 700;
		font-size: 12px;
		line-height: 16px;
		letter-spacing: 0;
		color: #ffffff;
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 8px 0 0 0 !important;
		clip-path: polygon(0 0, 100% 0, 100% calc(100% - 20px), calc(100% - 8px) 100%, 0 100%);
	}
	&__begin {
		font-family: 'Inter', sans-serif;
		font-weight: 700;
		font-size: 13px;
		line-height: 100%;
		letter-spacing: 0;
    white-space: nowrap;
	}
	&__end {
		font-family: 'Inter', sans-serif;
		font-weight: 400;
		font-size: 13px;
		line-height: 100%;
		letter-spacing: 0;
    white-space: nowrap;
	}

	&__title {
		font-family: 'Inter', sans-serif;
		overflow: hidden;
		text-overflow: ellipsis;
		display: -webkit-box;
		-webkit-box-orient: vertical;
		font-weight: 700;
		font-size: 16px;
		line-height: 100%;
		letter-spacing: 0;
	}

	&__event {
		&-title {
			display: flex;
			align-items: center;
		}

		&-text {
			font-size: 14px;
			color: #4a4a4a;
			line-height: 20px;
		}

		&-date {
			font-size: 14px;
			font-weight: 600;
			line-height: 17px;
		}

		&-location {
			display: flex;
			align-items: center;
			color: #0055a3;
			word-break: break-word;
			font-family: 'Inter', sans-serif;
			font-weight: 500;
			font-size: 13px;
			line-height: 100%;
			letter-spacing: 0;
		}

		&-track {
			font-size: 13px;
			font-weight: 700;
			line-height: 15.85px;
		}
	}

	&__add-agenda-btn {
		align-self: center;
		background-color: transparent !important;
		padding: 0 8px !important;
		min-width: auto !important;
	}
}
</style>
